import * as React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import {
	DragSource,
	DragSourceMonitor,
	DragSourceConnector,
	ConnectDragSource
} from "react-dnd";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";

import Typography from "@material-ui/core/Typography";
import { Reservation } from "../../api/reservations";
import { DropTargetData } from "./day-overview";
import { Dispatch } from "../../../node_modules/redux";
import { updateSingleReservation } from "../../actions/reservation-actions";
import { setActiveReservation } from "../../actions/store/overview-actions";
import { openEditReservationDialog } from "../../actions/store/app-actions";

interface State {}

export interface ComposerTableReservationProps extends Reservation {
	tableReservationCount: number;
}

interface ConnectedComposerTableReservationProps
	extends WithStyles<typeof styles> {
	activeReservationId: string;
	additionalInfoVisible: boolean;
	tableLinkModeActive: boolean;
	tableLinkDeletionModeActive: boolean;
	isDragging?: boolean;
	connectDragSource?: ConnectDragSource;
	setActiveReservation: (id: string) => void;
	openEditReservationDialog: (reservation: Reservation) => void;
}

type Props = ComposerTableReservationProps &
	ConnectedComposerTableReservationProps &
	InjectedTranslateProps;

export class ComposerTableReservation extends React.Component<Props, State> {
	public state: State = {};

	private lastClick = 0;

	handleMouseOver = () => {
		const { activeReservationId, setActiveReservation, id } = this.props;

		if (activeReservationId !== id) {
			setActiveReservation(id);
		}
	};

	openEditDialog() {
		const {
			id,
			tableId,
			date,
			peopleCount,
			primaryName,
			time,
			phoneNumber,
			email,
			checkedIn,
			notes,
			street,
			zip,
			city,
			country
		} = this.props;

		this.props.openEditReservationDialog({
			id,
			tableId,
			date,
			peopleCount,
			primaryName,
			time,
			phoneNumber,
			email,
			notes,
			checkedIn,
			street,
			zip,
			city,
			country
		});
	}

	handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
		event.preventDefault();

		this.openEditDialog();
	};

	handleClick = (
		event:
			| React.MouseEvent<HTMLDivElement>
			| React.TouchEvent<HTMLDivElement>
	) => {
		if (this.lastClick === 0) {
			this.lastClick = event.timeStamp;
		} else {
			const duration = event.timeStamp - this.lastClick;
			this.lastClick = 0;

			if (duration <= 250) {
				this.openEditDialog();
			}
		}
	};

	formatNameInfo(name: string, trim = false) {
		const splitName = name
			.trim()
			.split(" ")
			.pop();

		if (trim) {
			return splitName.substr(0, 5);
		} else {
			return splitName;
		}
	}

	renderDetailedReservation = (statusBookedOnline: boolean, statusPayed: boolean) => {
		const { classes, primaryName, peopleCount } = this.props;

		return (
			<div className={classes.detailedReservation}>
				<div className={classes.detailedReservationCountContainer}>
					<Typography className={classes.detailedReservationCount}>
						{peopleCount}
					</Typography>
				</div>
				<div className={classes.detailedReservationNameContainer}>
					<Typography className={classNames(classes.detailedReservationName, {
						[classes.detailedReservationNameStatusBookedOnline]: statusBookedOnline,
						[classes.detailedReservationNameStatusPayed]: statusPayed,
					})}>
						{this.formatNameInfo(primaryName)}
					</Typography>
				</div>
			</div>
		);
	};

	renderCompactReservation = () => {
		const {
			classes,
			primaryName,
			peopleCount,
			additionalInfoVisible
		} = this.props;

		return (
			<Typography
				className={classNames(classes.label, {
					[classes.labelSmall]: additionalInfoVisible
				})}
			>
				{additionalInfoVisible
					? this.formatNameInfo(primaryName, true)
					: peopleCount}
			</Typography>
		);
	};

	render() {
		const {
			classes,
			isDragging,
			connectDragSource,
			additionalInfoVisible,
			tableLinkModeActive,
			tableLinkDeletionModeActive,
			tableReservationCount,
			payment,
			inquiryId,
			checkedIn,
		} = this.props;

		const linkModeActive =
			tableLinkModeActive || tableLinkDeletionModeActive;

		const statusBookedOnline = Boolean(inquiryId);
		const statusPayed = payment && payment.state === "completed";

		return (
			connectDragSource &&
			connectDragSource(
				<div
					className={classNames(classes.root, {
						[classes.rootDisabled]: linkModeActive,
						[classes.rootDetailed]:
							tableReservationCount <= 12,
						[classes.rootDetailedFullWidth]:
							tableReservationCount <= 6,
					})}
				>
					<div
						className={classNames(classes.innerRoot, {
							[classes.innerRootDragged]: isDragging,
							[classes.innerRootBig]: additionalInfoVisible,
							[classes.innerRootDetailed]:
								tableReservationCount <= 12,
							[classes.innerRootStatusBookedOnline]: statusBookedOnline,
							[classes.innerRootStatusPayed]: statusPayed,
							[classes.innerRootCheckedIn]: checkedIn,
						})}
						onMouseOver={this.handleMouseOver}
						onClick={this.handleClick}
						onTouchStart={this.handleClick}
						onContextMenu={this.handleContextMenu}
					>
						{tableReservationCount <= 12
							? this.renderDetailedReservation(statusBookedOnline, statusPayed)
							: this.renderCompactReservation()}
					</div>
				</div>
			)
		);
	}
}

@DragSource(
	"ReservationItem",
	{
		beginDrag(props: Props) {
			return { id: props.id, peopleCount: props.peopleCount };
		},
		endDrag(props: Props, monitor: DragSourceMonitor) {
			const dropResult = monitor.getDropResult() as DropTargetData;

			if (dropResult) {
				updateSingleReservation(props.id, {
					tableId: dropResult.tableId
				});
			}
		}
	},
	(connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
		connectDragSource: connect.dragSource(),
		isDragging: monitor.isDragging()
	})
)
export class DecoratedComposerTableReservation extends ComposerTableReservation {}

const mapStateToProps = (state: StoreState) => ({
	activeReservationId: state.overviewActiveReservationId,
	additionalInfoVisible: state.overviewInfoVisible,
	tableLinkModeActive: state.tableLinkModeActive,
	tableLinkDeletionModeActive: state.tableLinkDeletionModeActive
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setActiveReservation: (id: string) => dispatch(setActiveReservation(id)),
	openEditReservationDialog: (reservation: Reservation) =>
		dispatch(openEditReservationDialog(reservation))
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: "flex",
			justifyContent: "center",
			width: "auto",
			minWidth: "46",
			height: 32,
			margin: 2
		},
		rootDetailed: {
			width: "48%",
			margin: 0
		},
		rootDetailedFullWidth: {
			width: "100%",
		},
		rootDisabled: {
			opacity: 0.5,
			pointerEvents: "none"
		},
		innerRoot: {
			width: 32,
			height: 32,
			borderRadius: "100%",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			cursor: "grab",
			position: "relative",
			background: theme.status.reservation.manual,
			boxShadow: "2px 2px 4px rgba(0, 0, 0, 0.2)",
			transition:
				"opacity 0.2s ease, width 0.2s ease, border-radius 0.2s ease, margin 0.2s ease"
		},
		innerRootStatusBookedOnline: {
			background: theme.status.reservation.bookedOnline
		},
		innerRootStatusPayed: {
			background: theme.status.reservation.payed
		},
		innerRootDragged: {
			opacity: 0.3
		},
		innerRootBig: {
			width: 46,
			borderRadius: 4
		},
		innerRootDetailed: {
			width: "90%",
			borderRadius: 4,
			flexWrap: "wrap",
			// background: "transparent",
			overflow: "hidden"
		},
		innerRootCheckedIn: {
			opacity: 0.3,
		},
		label: {
			color: "white"
		},
		labelSmall: {
			fontSize: "0.9rem"
		},
		detailedReservation: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			width: "100%",
			height: "100%"
		},
		detailedReservationCountContainer: {
			paddingRight: 5,
			paddingLeft: 5,
			// background: theme.status.reservation.manual,
			background: "rgba(0, 0, 0, 0.15)",
			height: "100%",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			width: 28,
			minWidth: 28
		},
		detailedReservationNameContainer: {
			paddingLeft: 5,
			paddingRight: 5,
			// background: theme.status.reservation.manual,
			height: "100%",
			display: "flex",
			justifyContent: "flex-start",
			alignItems: "center",
			flexGrow: 1,
			overflow: "hidden"
		},
		detailedReservationCount: {
			color: "white"
		},
		detailedReservationName: {
			color: "white",
			whiteSpace: "nowrap",
			width: "100%",
			textAlign: "center",
			position: "relative",
			overflow: "hidden",

			"&::after": {
				content: "''",
				position: "absolute",
				top: 0,
				right: 0,
				width: 6,
				height: "100%",
				background: `linear-gradient(to right, ${
					theme.status.reservation.manual
				}03 0%, ${theme.status.reservation.manual} 100%)`
			}
		},
		detailedReservationNameStatusBookedOnline: {
			"&::after": {
				background: `linear-gradient(to right, ${
					theme.status.reservation.bookedOnline
				}03 0%, ${theme.status.reservation.bookedOnline} 100%)`
			},
		},
		detailedReservationNameStatusPayed: {
			"&::after": {
				background: `linear-gradient(to right, ${
					theme.status.reservation.payed
				}03 0%, ${theme.status.reservation.payed} 100%)`
			},
		},
		tooltip: {
			position: "absolute",
			top: "50%",
			right: -20,
			opacity: 0,
			pointerEvents: "none",
			background: "#555555",
			padding: "0 8px 1px",
			borderRadius: 4,
			zIndex: 10,
			transform: "translate3d(100%, -50%, 0)",
			transition: "all 0.3s ease",

			"&:after": {
				content: "''",
				position: "absolute",
				top: "50%",
				left: -3,
				width: 7,
				height: 7,
				background: "#555555",
				transform: "translate3d(0, -50%, 0) rotate(-45deg)"
			}
		},
		tooltipVisible: {
			opacity: 1,
			right: -10
		},
		tooltipText: {
			color: "white",
			whiteSpace: "nowrap",
			background: "#555555",
			position: "relative",
			zIndex: 2
		}
	});

export const ConnectedComposerTableReservation = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(DecoratedComposerTableReservation)
	)
);
